/**
 * Edit Image（Figma `102:7930` / `102:7971`）。
 *
 * 圆环外接正方形导出 512。点遮罩不关闭；Esc、关闭钮、Cancel 在编码前丢掉这次选择。
 * 编码中这三个入口和 Done 都停用。
 *
 * 稿子上的背景模糊（弹窗 60、裁切区 30、关闭钮 6）不挂 `backdrop-filter`。
 * 装饰性滚动条是溢出装饰，内容放得下，不画。
 *
 * 预览裁在圆角舞台里，不裁在圆环所在的正方形上；圆环只标出导出范围，
 * 舞台其余部分仍能看见画面。滚轮在整个舞台上绕指针缩放。
 * 拖动平移有键盘替代：舞台获得焦点后，方向键平移，加号和减号绕中心缩放。
 * 舞台底是 Figma `Color/background/voice&video/secondary`（`#292929e5`）。
 * `gray-16` 只在暗色主题里，所以这里保留字面色。
 * 弹窗底是 Figma `Color/background/pop/pop`（白 98%）。`--app-base-white-a98` 同样只在亮色里，
 * 所以用 `bg-popover`（`--app-color-bg-surface`），暗色才能跟着主题走。
 */
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import {
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { cn } from '../../lib/utils';
import GalleryIcon from './avatar-editor-icons/gallery.svg?react';
import GalleryLargeIcon from './avatar-editor-icons/gallery-lg.svg?react';
import MirrorIcon from './avatar-editor-icons/mirror-horizontal.svg?react';
import RotateLeftIcon from './avatar-editor-icons/rotate-left.svg?react';
import { exportAvatarImage } from './avatar-image-editor-export';
import {
  avatarExportLimit,
  type AvatarPickProblem,
  inspectAvatarFile,
} from './avatar-image-editor-file';
import {
  AVATAR_CROP_SIZE,
  AVATAR_MAX_ZOOM,
  AVATAR_MIN_ZOOM,
  avatarCssMatrix,
  type AvatarView,
  flipAvatarHorizontal,
  initialAvatarView,
  panAvatar,
  rotateAvatarLeft,
  zoomAvatar,
  zoomFactorFromWheel,
} from './avatar-image-editor-math';
import { Button } from './button';
import { DialogOverlay, DialogPortal, DialogTitle } from './dialog';
import { Icon } from './icon';
import { Slider } from './slider';
import { Spinner } from './spinner';

export type AvatarEditorLabels = {
  title: string;
  reset: string;
  cancel: string;
  done: string;
  saving: string;
  notImage: string;
  tooManyFrames: string;
  undecodable: string;
  tooLarge: (sizeMb: number) => string;
  rotateLeft: string;
  flipHorizontal: string;
  zoom: string;
  /** 舞台的无障碍名称，说明拖动和键盘都能移动、缩放。 */
  stage: string;
};

export const DEFAULT_AVATAR_EDITOR_LABELS: AvatarEditorLabels = {
  title: 'Edit Image',
  reset: 'Reset',
  cancel: 'Cancel',
  done: 'Done',
  saving: 'Saving…',
  notImage: 'Choose an image file.',
  tooManyFrames: 'This GIF has too many frames.',
  undecodable: 'This image could not be opened.',
  tooLarge: (sizeMb) => `Image must be under ${sizeMb} MB.`,
  rotateLeft: 'Rotate left',
  flipHorizontal: 'Flip horizontal',
  zoom: 'Zoom',
  stage: 'Drag or use arrow keys to move. Plus and minus zoom.',
};

export type AvatarImageEditorInputProps = {
  ref: RefObject<HTMLInputElement | null>;
  type: 'file';
  accept: string;
  className: string;
  disabled?: boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

type Session = {
  file: File;
  url: string;
  kind: 'static' | 'gif';
  width: number;
  height: number;
};

const DRAG_SLOP_PX = 4;
const KEYBOARD_NUDGE_PX = 16;
const KEYBOARD_NUDGE_FAST_PX = 64;
const KEYBOARD_ZOOM_STEP = 0.1;

type StageDrag = {
  id: number;
  x: number;
  y: number;
  originX: number;
  originY: number;
  active: boolean;
};

/** 指针相对圆环外接正方形中心的位置，单位是导出像素。舞台比这个正方形大。 */
function cropPointer(
  crop: HTMLElement,
  clientX: number,
  clientY: number,
): { px: number; py: number; scale: number } | null {
  const rect = crop.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    return null;
  }
  return {
    px: ((clientX - rect.left) / rect.width - 0.5) * AVATAR_CROP_SIZE,
    py: ((clientY - rect.top) / rect.height - 0.5) * AVATAR_CROP_SIZE,
    scale: AVATAR_CROP_SIZE / rect.width,
  };
}

const TOOL_BUTTON =
  'flex size-(--app-base-size-15) cursor-pointer items-center justify-center rounded-full text-(--app-color-icon-primary) outline-none hover:bg-(--app-base-black-a8) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--app-base-brand) active:bg-(--app-base-black-a10) disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:active:bg-transparent dark:hover:bg-(--app-base-white-a10) dark:active:bg-(--app-base-white-a20)';

function nudgeStage(
  event: ReactKeyboardEvent<HTMLDivElement>,
  busy: boolean,
  imageWidth: number,
  imageHeight: number,
  onView: (update: (view: AvatarView) => AvatarView) => void,
): void {
  if (busy) {
    return;
  }
  const step = event.shiftKey ? KEYBOARD_NUDGE_FAST_PX : KEYBOARD_NUDGE_PX;
  if (event.key === 'ArrowLeft') {
    event.preventDefault();
    onView((current) => panAvatar(current, -step, 0, imageWidth, imageHeight));
    return;
  }
  if (event.key === 'ArrowRight') {
    event.preventDefault();
    onView((current) => panAvatar(current, step, 0, imageWidth, imageHeight));
    return;
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault();
    onView((current) => panAvatar(current, 0, -step, imageWidth, imageHeight));
    return;
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault();
    onView((current) => panAvatar(current, 0, step, imageWidth, imageHeight));
    return;
  }
  const zoomIn = event.key === '+' || event.key === '=' || event.key === 'Add';
  const zoomOut = event.key === '-' || event.key === 'Subtract';
  if (!zoomIn && !zoomOut) {
    return;
  }
  event.preventDefault();
  onView((current) =>
    zoomAvatar(
      current,
      current.zoom + (zoomIn ? KEYBOARD_ZOOM_STEP : -KEYBOARD_ZOOM_STEP),
      0,
      0,
      imageWidth,
      imageHeight,
    ),
  );
}

function sliderZoom(value: number | readonly number[]): number | undefined {
  const next = typeof value === 'number' ? value : value[0];
  return typeof next === 'number' ? next : undefined;
}

function resolveLabels(
  labels: Partial<AvatarEditorLabels> | undefined,
): AvatarEditorLabels {
  return { ...DEFAULT_AVATAR_EDITOR_LABELS, ...labels };
}

function problemText(
  problem: AvatarPickProblem,
  sizeMb: number | undefined,
  labels: AvatarEditorLabels,
): string {
  if (problem === 'tooLarge') {
    return labels.tooLarge(sizeMb ?? 20);
  }
  if (problem === 'tooManyFrames') {
    return labels.tooManyFrames;
  }
  if (problem === 'undecodable') {
    return labels.undecodable;
  }
  return labels.notImage;
}

export function useAvatarImageEditor({
  onAccept,
  labels: labelOverrides,
  onNotify,
  disabled = false,
}: {
  onAccept: (file: File) => void;
  labels?: Partial<AvatarEditorLabels>;
  onNotify: (message: string) => void;
  disabled?: boolean;
}): {
  inputProps: AvatarImageEditorInputProps;
  openFilePicker: () => void;
  dialog: ReactNode;
} {
  const labels = resolveLabels(labelOverrides);
  const inputRef = useRef<HTMLInputElement>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [view, setView] = useState<AvatarView>(initialAvatarView);
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef(session);
  const viewRef = useRef(view);
  const busyRef = useRef(busy);
  const mountedRef = useRef(true);
  const requestRef = useRef(0);
  sessionRef.current = session;
  viewRef.current = view;
  busyRef.current = busy;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const current = sessionRef.current;
      if (current) {
        URL.revokeObjectURL(current.url);
      }
    };
  }, []);

  function discard(): void {
    requestRef.current += 1;
    setSession((current) => {
      if (current) {
        URL.revokeObjectURL(current.url);
      }
      return null;
    });
    setView(initialAvatarView());
    setBusy(false);
  }

  function onChange(event: ChangeEvent<HTMLInputElement>): void {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || disabled) {
      return;
    }
    const ticket = requestRef.current + 1;
    requestRef.current = ticket;
    void inspectAvatarFile(file).then((pick) => {
      if (!mountedRef.current || ticket !== requestRef.current) {
        return;
      }
      if (!pick.ok) {
        onNotify(problemText(pick.problem, pick.sizeMb, labels));
        return;
      }
      const url = URL.createObjectURL(file);
      setSession((current) => {
        if (current) {
          URL.revokeObjectURL(current.url);
        }
        return {
          file,
          url,
          kind: pick.kind,
          width: pick.width,
          height: pick.height,
        };
      });
      setView(initialAvatarView());
    });
  }

  function confirm(): void {
    const current = sessionRef.current;
    if (!current || busyRef.current) {
      return;
    }
    const ticket = requestRef.current + 1;
    requestRef.current = ticket;
    setBusy(true);
    void exportAvatarImage(current.file, viewRef.current, current.kind)
      .then((exported) => {
        if (!mountedRef.current || ticket !== requestRef.current) {
          return;
        }
        const limit = avatarExportLimit(current.kind);
        if (exported.size > limit.bytes) {
          onNotify(labels.tooLarge(limit.sizeMb));
          return;
        }
        onAccept(exported);
        discard();
      })
      .catch(() => {
        if (mountedRef.current && ticket === requestRef.current) {
          onNotify(labels.undecodable);
        }
      })
      .finally(() => {
        if (mountedRef.current && ticket === requestRef.current) {
          setBusy(false);
        }
      });
  }

  return {
    inputProps: {
      ref: inputRef,
      type: 'file',
      accept: 'image/*',
      className: 'hidden',
      disabled,
      onChange,
    },
    openFilePicker: () => {
      if (!disabled) {
        inputRef.current?.click();
      }
    },
    dialog: (
      <AvatarImageEditorDialog
        session={session}
        view={view}
        busy={busy}
        labels={labels}
        onView={setView}
        onReset={() => setView(initialAvatarView())}
        onDismiss={() => {
          if (!busyRef.current) {
            discard();
          }
        }}
        onConfirm={confirm}
      />
    ),
  };
}

// The hook, labels, and dialog stay in one module; fast refresh isn't the consumer path.
// eslint-disable-next-line react-refresh/only-export-components
function AvatarImageEditorDialog({
  session,
  view,
  busy,
  labels,
  onView,
  onReset,
  onDismiss,
  onConfirm,
}: {
  session: Session | null;
  view: AvatarView;
  busy: boolean;
  labels: AvatarEditorLabels;
  onView: (update: (view: AvatarView) => AvatarView) => void;
  onReset: () => void;
  onDismiss: () => void;
  onConfirm: () => void;
}) {
  const cropRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<StageDrag | null>(null);
  const wheelTarget = useRef<{
    node: HTMLDivElement;
    stop: () => void;
  } | null>(null);
  const sessionRef = useRef(session);
  const busyRef = useRef(busy);
  const onViewRef = useRef(onView);
  sessionRef.current = session;
  busyRef.current = busy;
  onViewRef.current = onView;
  const open = session !== null;

  // 弹层内容可能晚于本次 effect 才挂上，回调 ref 在节点提交时绑定。
  // React 的 onWheel 是 passive，拦不住页面滚动。
  const frameRef = useCallback((node: HTMLDivElement | null) => {
    const previous = wheelTarget.current;
    if (previous?.node === node) {
      return;
    }
    previous?.stop();
    wheelTarget.current = null;
    if (!node) {
      return;
    }
    const onWheel = (event: WheelEvent) => {
      const current = sessionRef.current;
      const crop = cropRef.current;
      if (!current || !crop) {
        return;
      }
      event.preventDefault();
      if (busyRef.current) {
        return;
      }
      const point = cropPointer(crop, event.clientX, event.clientY);
      if (!point) {
        return;
      }
      onViewRef.current((view) =>
        zoomAvatar(
          view,
          view.zoom * zoomFactorFromWheel(event.deltaY),
          point.px,
          point.py,
          current.width,
          current.height,
        ),
      );
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    wheelTarget.current = {
      node,
      stop: () => node.removeEventListener('wheel', onWheel),
    };
  }, []);

  return (
    <DialogPrimitive.Root
      open={open}
      disablePointerDismissal
      onOpenChange={(next) => {
        if (!next) {
          onDismiss();
        }
      }}
    >
      <DialogPortal>
        <DialogOverlay className="bg-(--app-base-black-a40)" />
        <DialogPrimitive.Popup
          data-avatar-image-editor=""
          aria-busy={busy}
          className="fixed top-1/2 left-1/2 z-50 w-[564px] max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 rounded-(--app-base-radius-20) border border-(--app-base-white-a30) bg-popover shadow-[0_0_16px_rgba(0,0,0,0.12)] outline-none transition-[opacity,transform] duration-150 ease-out data-ending-style:scale-95 data-ending-style:opacity-0 data-starting-style:scale-95 data-starting-style:opacity-0"
        >
          {session ? (
            <EditorBody
              session={session}
              view={view}
              busy={busy}
              labels={labels}
              frameRef={frameRef}
              cropRef={cropRef}
              dragRef={dragRef}
              onView={onView}
              onReset={onReset}
              onDismiss={onDismiss}
              onConfirm={onConfirm}
            />
          ) : null}
        </DialogPrimitive.Popup>
      </DialogPortal>
    </DialogPrimitive.Root>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
function EditorBody({
  session,
  view,
  busy,
  labels,
  frameRef,
  cropRef,
  dragRef,
  onView,
  onReset,
  onDismiss,
  onConfirm,
}: {
  session: Session;
  view: AvatarView;
  busy: boolean;
  labels: AvatarEditorLabels;
  frameRef: Ref<HTMLDivElement | null>;
  cropRef: RefObject<HTMLDivElement | null>;
  dragRef: RefObject<StageDrag | null>;
  onView: (update: (view: AvatarView) => AvatarView) => void;
  onReset: () => void;
  onDismiss: () => void;
  onConfirm: () => void;
}) {
  const { width, height } = session;
  return (
    <>
      <header className="relative h-(--app-base-size-20)">
        <DialogTitle className="absolute top-(--app-base-size-11) right-(--app-base-size-16) left-(--app-base-size-13) text-left text-(length:--app-base-font-size-16) leading-(--app-base-font-line-24) font-medium text-(--app-color-text-primary)">
          {labels.title}
        </DialogTitle>
        <DialogPrimitive.Close
          type="button"
          aria-label={labels.cancel}
          disabled={busy}
          className="absolute top-(--app-base-size-7) right-(--app-base-size-7) flex size-(--app-base-size-16) cursor-pointer items-center justify-center rounded-full text-(--app-color-icon-primary) outline-none hover:bg-(--app-base-black-a8) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--app-base-brand) active:bg-(--app-base-black-a10) disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:active:bg-transparent dark:hover:bg-(--app-base-white-a10) dark:active:bg-(--app-base-white-a20)"
        >
          <Icon spec={{ type: 'builtin', id: 'i-cancel' }} size={24} />
        </DialogPrimitive.Close>
      </header>
      <div className="mt-(--app-base-size-9) flex flex-col gap-(--app-base-size-18) px-(--app-base-size-16) pb-(--app-base-size-13)">
        <div>
          <div
            ref={frameRef}
            data-avatar-editor-frame=""
            tabIndex={busy ? -1 : 0}
            role="group"
            aria-label={labels.stage}
            aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight Plus Minus"
            className={cn(
              'relative touch-none overflow-hidden rounded-(--app-base-radius-20) bg-[rgba(41,41,41,0.9)] py-(--app-base-size-12) outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--app-base-brand)',
              busy
                ? 'cursor-not-allowed'
                : 'cursor-grab active:cursor-grabbing',
            )}
            onPointerDown={(event) => {
              if (busy || event.button !== 0) {
                return;
              }
              event.currentTarget.focus();
              dragRef.current = {
                id: event.pointerId,
                x: event.clientX,
                y: event.clientY,
                originX: event.clientX,
                originY: event.clientY,
                active: false,
              };
              if (typeof event.currentTarget.setPointerCapture === 'function') {
                event.currentTarget.setPointerCapture(event.pointerId);
              }
            }}
            onPointerMove={(event) => {
              const drag = dragRef.current;
              const crop = cropRef.current;
              if (busy || !drag || !crop || drag.id !== event.pointerId) {
                return;
              }
              if (!drag.active) {
                const travel = Math.hypot(
                  event.clientX - drag.originX,
                  event.clientY - drag.originY,
                );
                if (travel < DRAG_SLOP_PX) {
                  return;
                }
                drag.active = true;
              }
              const point = cropPointer(crop, event.clientX, event.clientY);
              if (!point) {
                return;
              }
              const dx = (event.clientX - drag.x) * point.scale;
              const dy = (event.clientY - drag.y) * point.scale;
              drag.x = event.clientX;
              drag.y = event.clientY;
              onView((current) => panAvatar(current, dx, dy, width, height));
            }}
            onPointerUp={() => {
              dragRef.current = null;
            }}
            onPointerCancel={() => {
              dragRef.current = null;
            }}
            onKeyDown={(event) => {
              nudgeStage(event, busy, width, height, onView);
            }}
          >
            <div
              ref={cropRef}
              data-avatar-editor-crop=""
              className="relative mx-auto size-[300px]"
            >
              <img
                src={session.url}
                alt=""
                draggable={false}
                className="pointer-events-none absolute top-0 left-0 max-w-none"
                style={{
                  width: width,
                  height: height,
                  transformOrigin: '0 0',
                  transform: avatarCssMatrix(view, width, height, 300),
                }}
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 rounded-full border-(length:--app-base-size-4) border-(--app-base-white-a80) shadow-[0_0_0_9999px_var(--app-base-black-a40)]"
              />
            </div>
          </div>
          <div className="mt-(--app-base-size-15) flex items-center gap-(--app-base-size-12)">
            <div className="flex min-w-0 flex-1 items-center gap-(--app-base-size-9)">
              <span className="flex size-(--app-base-size-11) shrink-0 items-center justify-center text-(--app-color-icon-primary)">
                <GalleryIcon />
              </span>
              <Slider
                aria-label={labels.zoom}
                min={AVATAR_MIN_ZOOM}
                max={AVATAR_MAX_ZOOM}
                step={0.01}
                value={[view.zoom]}
                disabled={busy}
                className="[&_[data-slot=slider-thumb]]:border-(--app-color-border) [&_[data-slot=slider-thumb]]:bg-(--app-color-bg-surface) [&_[data-slot=slider-track]]:h-(--app-base-size-3)"
                onValueChange={(value) => {
                  const next = sliderZoom(value);
                  if (next === undefined) {
                    return;
                  }
                  onView((current) =>
                    zoomAvatar(current, next, 0, 0, width, height),
                  );
                }}
              />
              <span className="flex size-(--app-base-size-14) shrink-0 items-center justify-center text-(--app-color-icon-primary)">
                <GalleryLargeIcon />
              </span>
            </div>
            <div className="flex h-(--app-base-size-17) w-[104px] shrink-0 items-center justify-center gap-(--app-base-size-5) rounded-(--app-base-radius-14) bg-(--app-base-black-a4) px-(--app-base-size-7)">
              <button
                type="button"
                aria-label={labels.rotateLeft}
                disabled={busy}
                onClick={() =>
                  onView((current) => rotateAvatarLeft(current, width, height))
                }
                className={TOOL_BUTTON}
              >
                <RotateLeftIcon />
              </button>
              <button
                type="button"
                aria-label={labels.flipHorizontal}
                disabled={busy}
                onClick={() =>
                  onView((current) =>
                    flipAvatarHorizontal(current, width, height),
                  )
                }
                className={TOOL_BUTTON}
              >
                <MirrorIcon />
              </button>
            </div>
          </div>
        </div>
        <footer className="flex h-(--app-base-size-16) items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={onReset}
            className="h-auto px-0 text-(length:--app-base-font-size-16) leading-(--app-base-font-line-24) font-medium text-(--app-color-accent) hover:bg-transparent hover:text-(--app-color-brand-hover)"
          >
            {labels.reset}
          </Button>
          <div className="flex gap-(--app-base-size-6)">
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={onDismiss}
              className="h-(--app-base-size-16) w-[148px] rounded-(--app-base-radius-12) bg-(--app-base-black-a6) text-(length:--app-base-font-size-16) leading-(--app-base-font-line-24) font-medium text-(--app-color-text-primary) hover:bg-(--app-base-black-a6)"
            >
              {labels.cancel}
            </Button>
            <Button
              type="button"
              disabled={busy}
              aria-busy={busy}
              onClick={onConfirm}
              className="h-(--app-base-size-16) w-[148px] rounded-(--app-base-radius-12) text-(length:--app-base-font-size-16) leading-(--app-base-font-line-24) font-medium"
            >
              {busy ? <Spinner aria-label={labels.saving} /> : null}
              {busy ? labels.saving : labels.done}
            </Button>
          </div>
        </footer>
      </div>
    </>
  );
}
